import type { IComponentController, IFilterService, IScope } from "angular";
import { isNil } from "lodash";
import type { Duration } from "moment-timezone";
import { duration } from "moment-timezone";

import type { IResultDaySchedule } from "App/Modules/Models";
import { ScheduleTypes } from "App/Modules/Models";

import type IInterval from "./IInterval";

const DAY_IN_SECONDS = 86400;

export default class TimeRangeResultComponentController implements IComponentController {
    public static $inject: string[] = ["$scope", "$filter", "$translate"];

    public baseColorTheme: string;

    public resultDaySchedule: IResultDaySchedule;

    public isDisabled: boolean;

    public lowResolutionMode: boolean;

    public mappedRanges: IInterval[];

    public intervalsCount: number;

    public intervalLength: number;

    public intervalHeight: number;

    public baseScheduleType: string;

    constructor(
        private readonly $scope: IScope,
        private readonly $filter: IFilterService,
        private readonly $translate: ng.translate.ITranslateService,
    ) {
        // eslint-disable-next-line @typescript-eslint/unbound-method
        this.tooltipMapper = this.tooltipMapper.bind(this);
    }

    public $onInit() {
        this.intervalsCount = 1440;
        this.intervalLength = DAY_IN_SECONDS / this.intervalsCount / 60;
        this.intervalHeight = 25;

        this.baseScheduleType = ScheduleTypes[this.resultDaySchedule.baseSchedule.type].toLowerCase();

        this.mappedRanges = this.mapSections(this.resultDaySchedule);
    }

    private mapSections(schedule: IResultDaySchedule) {
        let allMappedRanges = schedule.baseSchedule.timePeriods
            .map((period) => {
                return {
                    color: schedule.baseSchedule.color,
                    isOn: true,
                    start: this.fromDurationToNumber(period.startTime),
                    stop: this.fromDurationToNumber(period.stopTime.subtract(1, "s")),
                    type: ScheduleTypes[schedule.baseSchedule.type].toLowerCase(),
                };
            })
            .concat(
                schedule.scheduleExceptions.map((exception) => {
                    return {
                        color: exception.color,
                        isOn: exception.isOnType,
                        start: this.fromDurationToNumber(exception.start),
                        stop: this.fromDurationToNumber(exception.stop),
                        type: ScheduleTypes[ScheduleTypes.ExceptionSchedule].toLowerCase(),
                    };
                }),
            );

        if (schedule.holidaySchedule) {
            allMappedRanges = allMappedRanges.concat(
                schedule.holidaySchedule.timePeriods.map((period) => {
                    return {
                        color: schedule.holidaySchedule.color,
                        isOn: true,
                        start: this.fromDurationToNumber(period.startTime),
                        stop: this.fromDurationToNumber(period.stopTime.subtract(1, "s")),
                        type: ScheduleTypes[ScheduleTypes.HolidaySchedule].toLowerCase(),
                    };
                }),
            );
        }

        const inactiveExceptions = allMappedRanges.filter(
            (range) => !range.isOn && range.type === ScheduleTypes[ScheduleTypes.ExceptionSchedule].toLowerCase(),
        );

        const activeExceptions = allMappedRanges.filter(
            (range) => range.isOn && range.type === ScheduleTypes[ScheduleTypes.ExceptionSchedule].toLowerCase(),
        );

        const holidayScheduleRanges = allMappedRanges.filter(
            (range) => range.type === ScheduleTypes[ScheduleTypes.HolidaySchedule].toLowerCase(),
        );

        const baseScheduleRanges = allMappedRanges.filter(
            (range) => range.type === ScheduleTypes[schedule.baseSchedule.type].toLowerCase(),
        );

        return this.getAdjustedMappedRanges(
            inactiveExceptions,
            activeExceptions,
            holidayScheduleRanges,
            baseScheduleRanges,
        );
    }

    private getAdjustedMappedRanges(
        inactiveExceptions: IInterval[],
        activeExceptions: IInterval[],
        holidayScheduleRanges: IInterval[],
        baseScheduleRanges: IInterval[],
    ) {
        const emptySchedule: IInterval[] = [];
        emptySchedule.push({
            color: this.resultDaySchedule.baseSchedule.color,
            isOn: false,
            start: 0,
            stop: this.intervalsCount - 1,
            type: this.baseScheduleType,
        });

        const ranges = inactiveExceptions;

        activeExceptions
            .concat(holidayScheduleRanges)
            .concat(baseScheduleRanges)
            .concat(emptySchedule)
            .forEach((range) => {
                let startPosition = -1;
                let stopPosition = -1;

                for (let i = range.start; i <= range.stop; i++) {
                    if (!this.isWithinAnyRange(i, ranges)) {
                        if (startPosition === -1) {
                            startPosition = i;
                        }

                        if (i === range.stop) {
                            stopPosition = i;
                        }
                    } else if (startPosition !== -1) {
                        stopPosition = i - 1;
                    }

                    if (startPosition !== -1 && stopPosition !== -1) {
                        ranges.push({
                            color: range.color,
                            isOn: range.isOn,
                            start: startPosition,
                            stop: stopPosition,
                            type: range.type,
                        } as IInterval);

                        startPosition = -1;
                        stopPosition = -1;
                    }
                }
            });

        return ranges;
    }

    private isWithinAnyRange(position: number, ranges: IInterval[]) {
        return ranges.some((range) => position >= range.start && position <= range.stop);
    }

    private fromDurationToNumber(time: Duration) {
        return Math.floor((time.asSeconds() / DAY_IN_SECONDS) * this.intervalsCount);
    }

    private fromNumberToStartDuration(position: number) {
        return duration(position * this.intervalLength, "m");
    }

    private fromNumberToStopDuration(position: number) {
        return duration(position * this.intervalLength, "m").add(this.intervalLength, "m");
    }

    private getCorrespondingRange(position: number) {
        let correspondingRange: IInterval;
        correspondingRange = this.mappedRanges.find(
            (range) =>
                range.type === ScheduleTypes[ScheduleTypes.ExceptionSchedule].toLowerCase() &&
                range.start <= position &&
                range.stop >= position,
        );

        if (isNil(correspondingRange)) {
            correspondingRange = this.mappedRanges.find(
                (range) =>
                    range.type !== ScheduleTypes[ScheduleTypes.ExceptionSchedule].toLowerCase() &&
                    range.start <= position &&
                    range.stop >= position,
            );
        }

        if (isNil(correspondingRange)) {
            let startPosition = 0;
            let stopPosition = 0;

            this.mappedRanges.forEach((range) => {
                startPosition = startPosition < range.stop && position > range.stop ? range.stop : startPosition;
                stopPosition = stopPosition < range.start && position < range.start ? range.start : stopPosition;
            });

            return {
                color: this.resultDaySchedule.baseSchedule.color,
                isOn: false,
                start: startPosition,
                stop: stopPosition,
                type: this.baseScheduleType,
            } as IInterval;
        }

        return correspondingRange;
    }

    private tooltipMapper(position: number) {
        const durationFilter: (duration: Duration) => string = this.$filter("mmDurationToLocalTimeString");

        const correspondingRange = this.getCorrespondingRange(position);

        const startString = durationFilter(this.fromNumberToStartDuration(correspondingRange.start));
        const stopString = durationFilter(this.fromNumberToStopDuration(correspondingRange.stop));
        const timeSpanString = `${startString} - ${stopString}`;
        const text = `${this.$translate.instant(
            "Gui.Common.ScheduleTooltip.AlarmStatus",
            { alarmStatus: correspondingRange.isOn },
            "messageformat",
        )}: ${timeSpanString}`;

        return text;
    }
}
